import { z } from 'zod';

/**
 * „Co nowego” — panel po zalogowaniu, gdy pojawiła się nowa wersja (raz na osobę; historia w menu użytkownika).
 * Wpis dopisuje się przy każdym wydaniu (deploy/wydaj.sh nie wyda wersji bez nowego wpisu): najnowszy na górze,
 * krótkie punkty prostym językiem z punktu widzenia użytkownika. Obszar decyduje, kto widzi punkt.
 */

/** Obszar punktu → uprawnienia (wystarczy jedno); null — wszyscy. */
export const RELEASE_AREAS = {
  ogolne: { label: 'Ogólne', permissions: null },
  sprawy: { label: 'Sprawy', permissions: ['sprawy.podglad'] },
  oferty: { label: 'Oferty', permissions: ['oferty.podglad'] },
  umowy: { label: 'Umowy', permissions: ['umowy.podglad'] },
  planowanie: { label: 'Planowanie i ekipy', permissions: ['planowanie.edycja', 'ekipy.zarzadzanie'] },
  finanse: { label: 'Finanse', permissions: ['finanse.podglad', 'faktury.podglad'] },
  strefa: { label: 'Strefa klienta', permissions: ['sprawy.podglad'] },
  serwis: { label: 'Serwis', permissions: ['serwis.podglad', 'serwis.platnosci'] },
  teren: { label: 'Aplikacja terenowa', permissions: ['zadania.status', 'uzytkownicy.zarzadzanie'] },
  ustawienia: { label: 'Ustawienia i pracownicy', permissions: ['uzytkownicy.zarzadzanie'] },
} as const satisfies Record<string, { label: string; permissions: readonly string[] | null }>;
export type ReleaseArea = keyof typeof RELEASE_AREAS;

export interface ReleaseNote {
  /** RRRR-MM-DD, przy kilku wydaniach jednego dnia z przyrostkiem (-2, -3) — rośnie z każdym wydaniem */
  id: string;
  items: { area: ReleaseArea; text: string }[];
}

export const RELEASE_NOTES: ReleaseNote[] = [
  {
    id: '2026-11-05',
    items: [
      { area: 'finanse', text: 'Serwis do zafakturowania: na liście i w powiadomieniach są też zgłoszenia wykonane, ale niezamknięte — gdy faktura nie wystawiła się automatycznie, klient nie płaci od 7 dni od linku albo dzień po wizycie nie ma wpłaty ani wysłanego linku. Numer faktury wystawionej ręcznie można wpisać już przy zgłoszeniu wykonanym — zgłoszenie zamyka się wtedy samo.' },
      { area: 'serwis', text: 'Pozycje do zapłaty dodane w biurze przed wizytą (np. wizyta ekipy bez cen): link „Zapłać online” wysyła się sam po podpisaniu protokołu. Klient bez e-maila dostanie link sam, gdy ktoś dopisze adres.' },
      { area: 'planowanie', text: 'Blokada zgłoszona z terenu na wizycie serwisowej („Wstrzymano prace”, „Prace wstrzymane od N dni”) trafia też do koordynatora i biura serwisu. Koordynator bez modułu Serwis otwiera z powiadomienia zadanie w planowaniu.' },
      { area: 'serwis', text: 'Instalacje: przycisk „Nowa instalacja” — instalację sprzed wdrożenia CRM dodasz ręcznie (lokalizacja klienta, branża, firma, nazwa), a zaraz po zapisie jej urządzenia z gwarancją.' },
    ],
  },
  {
    id: '2026-11-04-13',
    items: [
      { area: 'finanse', text: 'Faktury sprzedaży: faktura za serwis ma „Odśwież z Fakturowni” i sekcję „Wszystkie dokumenty tego zgłoszenia” — z numerem, datą, brutto, stanem opłacenia, statusem KSeF i wysyłką e-mailem, jak dokumenty umowy. Kwoty, daty i KSeF pochodzą z Fakturowni.' },
      { area: 'finanse', text: 'Status KSeF faktur (z umów i za serwis) w trakcie przetwarzania odświeża się sam co 15 minut, także przy fakturach już opłaconych. Rejestr faktur sprzedaży znajduje fakturę za serwis także po numerze KSeF.' },
    ],
  },
  {
    id: '2026-11-04-12',
    items: [
      { area: 'teren', text: 'Aplikacja terenowa, wpis w „Moim kalendarzu”: godziny wybiera się stuknięciami (godzina, minuty co 15) zamiast wpisywać z klawiatury. Przy godzinie „do” są skróty czasu trwania (30 min – 3 h), a zmiana godziny „od” przesuwa koniec o tyle samo.' },
      { area: 'teren', text: 'Sprzedaż w terenie powyżej progu umowy: wycena, karta „Powyżej progu umowy” i komunikat po przekazaniu do biura mówią wprost, że termin montażu nie jest zarezerwowany — ustali go biuro przy umowie (wcześniej wybrany termin znikał bez wyjaśnienia).' },
      { area: 'finanse', text: 'Faktury sprzedaży: faktura za serwis otwiera się w tym samym oknie co faktura z umowy — z pozycjami, „Pobierz PDF” i „Wyślij do klienta”. Link prowadzi do jej źródła (zgłoszenia albo umowy).' },
      { area: 'finanse', text: 'Faktura końcowa / VAT o grosz–dwa wyższa niż harmonogram (zaokrąglenie w Fakturowni) nie wisi już jako nieopłacona bez wyjścia: tę resztę można zarejestrować przy fakturze („Zarejestruj wpłatę”).' },
    ],
  },
  {
    id: '2026-11-04-11',
    items: [
      { area: 'finanse', text: 'Faktury za serwis są w „Faktury sprzedaży” razem z fakturami z umów — w wyszukiwaniu (numer, nabywca, numer zgłoszenia), filtrach, sumach i eksporcie CSV. Szczegóły faktury prowadzą do zgłoszenia.' },
      { area: 'strefa', text: 'Klient widzi fakturę za serwis w strefie klienta w „Dokumentach finansowych” i może pobrać jej PDF.' },
      { area: 'serwis', text: 'Nowe zgłoszenie: jedno pole wyszukiwania klienta i lokalizacji — po nazwisku klienta, adresie, numerze sprawy albo telefonie.' },
      { area: 'serwis', text: 'Rozliczenie naprawy (gwarancja / płatna / bez opłaty) wybiera się od razu przy dodawaniu zgłoszenia.' },
      { area: 'serwis', text: 'Wykonanego zgłoszenia nie można już edytować; rozliczenie i reklamację u producenta nadal zmienisz w oknie zgłoszenia (z powodem).' },
      { area: 'serwis', text: 'Zgłoszenie z podpisanym protokołem i fakturą za serwis wystawioną przez CRM zamyka się samo po zakończeniu wizyty.' },
      { area: 'oferty', text: 'Pulpit: nowy element „Najbliższe wolne terminy” (Dostosuj pulpit → Dodaj element). Pokazuje 5 najbliższych wolnych dni ekip w Twoich branżach, z wyborem branży i długości prac (1–5 dni). Handlowiec widzi dni i liczbę wolnych ekip; termin rezerwuje koordynator.' },
      { area: 'planowanie', text: 'Pulpit, „Najbliższe wolne terminy”: koordynator widzi przy każdym terminie nazwę wolnej ekipy, według tych samych zasad co w sprzedaży w terenie.' },
    ],
  },
  {
    id: '2026-11-04-10',
    items: [
      { area: 'teren', text: 'Aplikacja terenowa w przeglądarce: „Rozpocznij” po umówionych godzinach pyta o potwierdzenie i rozpoczyna zadanie (wcześniej kliknięcie nic nie robiło). Koordynator dostaje informację o starcie po terminie.' },
    ],
  },
  {
    id: '2026-11-04-9',
    items: [
      { area: 'teren', text: 'Sprzedaż w terenie: zapłata na miejscu od razu wystawia dokument w Fakturowni — przy zaliczce fakturę zaliczkową, przy zapłacie całości fakturę VAT, obie jako opłacone. Tak samo po zapłacie z linku „Klient zapłaci później”.' },
    ],
  },
  {
    id: '2026-11-04-8',
    items: [
      { area: 'teren', text: 'Serwis i przeglądy: serwisant dodaje w protokole pozycje do zapłaty z cennika serwisu (albo spoza cennika z własną ceną), klient potwierdza je podpisem i płaci od razu kodem QR ING Pay (BLIK, karta). „Klient zapłaci później” wysyła mu link e-mailem; bez internetu link wyśle CRM po synchronizacji.' },
      { area: 'teren', text: 'Przegląd gwarancyjny jest bezpłatny (pozycja „w ramach gwarancji”, 0 zł); płatne pozycje przy gwarancji dopisuje się z powodem.' },
      { area: 'serwis', text: 'Okno zgłoszenia → „Do zapłaty”: pozycje z protokołów, wpłata online, „Wyślij link do zapłaty” i stan faktury. Pozycje spoza cennika są oznaczone.' },
      { area: 'finanse', text: 'Po pełnej wpłacie online za serwis CRM sam wystawia w Fakturowni fakturę jako zapłaconą i wysyła ją klientowi. Gdy się nie uda (np. brak adresu klienta), księgowość dostaje powiadomienie z powodem.' },
      { area: 'oferty', text: 'Katalog: przełącznik „Cennik serwisu” — usługi z cennika serwisant wybiera na telefonie.' },
    ],
  },
  {
    id: '2026-11-04-7',
    items: [
      { area: 'sprawy', text: 'Karta sprawy, „Co teraz?”: gdy masz w sprawie kilka zadań (np. rozliczenie zaliczki i zaplanowanie realizacji), każde jest w osobnym wierszu — krótki tytuł, pod nim szczegóły (czego brakuje, termin) i własny przycisk. Przy zadaniu „do przejęcia” jest też „Przejmij”.' },
    ],
  },
  {
    id: '2026-11-04-6',
    items: [
      { area: 'strefa', text: 'Po zapłacie pro formy online strona płatności pokazuje „Opłacone” (wcześniej mylący komunikat „Płatność online niedostępna”), a gdy płatność zaczęła się w strefie klienta — przycisk „Wróć do strefy klienta”.' },
    ],
  },
  {
    id: '2026-11-04-5',
    items: [
      { area: 'finanse', text: 'Gdy klient zapłaci pro formę online (ze strefy klienta albo z linku w e-mailu), faktura zaliczkowa na tę wpłatę wystawia się sama — od razu jako opłacona — i trafia do klienta jak po „Wystaw fakturę”.' },
    ],
  },
  {
    id: '2026-11-04-4',
    items: [
      { area: 'ustawienia', text: 'Firmy → edycja firmy → „Logo do dokumentów”: wgraj logo (PNG albo JPG, do 1 MB). Logo pojawia się w ofertach i protokołach z układu wbudowanego oraz wszędzie, gdzie szablon ma pole „Logo firmy”.' },
      { area: 'umowy', text: 'Szablony dokumentów: przycisk „Logo i grafika” wstawia logo firmy wystawiającej dokument, a „Wgraj grafikę” — podpis, pieczęć albo baner (PNG, JPG do 1 MB). Rozmiar wybiera się w tym samym menu, położenie — przyciskami wyrównania. Grafiki widać w podglądzie i w PDF.' },
      { area: 'oferty', text: 'Oferta ma logo firmy w nagłówku, gdy firma wgrała logo. Wydana oferta, umowa i protokół zachowują logo z chwili wydania, nawet po jego zmianie.' },
    ],
  },
  {
    id: '2026-11-04-3',
    items: [
      { area: 'finanse', text: 'Płatności online ING Pay: klient zapłaci pro formę albo fakturę BLIK-iem, kartą lub szybkim przelewem z linku w e-mailu, z przypomnienia o płatności i ze strefy klienta. Wpłata zapisuje się sama (sposób „Online (ING Pay)”), tak jak przelew.' },
      { area: 'finanse', text: 'Gdy klient zapłaci online więcej, niż zostało (np. dwa razy), nadwyżka trafia do zadania „Zwrot nadpłaty z płatności online” — zwrot robi się w panelu ING Pay, potem „Zwrócono” w zakładce Płatności sprawy.' },
      { area: 'strefa', text: 'W strefie klienta przy dokumencie do zapłaty jest przycisk „Zapłać online” na pozostałą kwotę.' },
      { area: 'teren', text: 'Sprzedaż w terenie: płatność na miejscu kodem QR ING Pay (klient płaci na swoim telefonie) albo „Otwórz tutaj”; „Klient zapłaci później” wysyła mu link do płatności e-mailem.' },
      { area: 'ustawienia', text: 'Firmy → „Płatności online (ING Pay)”: tryb sandbox / produkcja, dane sklepu i „Sprawdź połączenie”. Bez konfiguracji klienci nie widzą przycisków płatności.' },
    ],
  },
  {
    id: '2026-11-04-2',
    items: [
      { area: 'teren', text: 'Aplikacja terenowa: karta zadania na ekranie „Praca” mówi wprost, czy zadanie jest na dziś, czy na jutro (z datą), a dzisiejsze zadania są w sekcji „Dziś”.' },
    ],
  },
  {
    id: '2026-11-04',
    items: [
      { area: 'oferty', text: 'Cenę pozycji można wpisać brutto — netto przelicza się samo według stawki VAT pozycji.' },
      { area: 'oferty', text: 'Przy pozycji z rabatem na całość widać jej wartość przed tym rabatem — od razu wiadomo, skąd niższa kwota.' },
      { area: 'oferty', text: 'W katalogu cena sprzedaży brutto: na liście i w formularzu (wpisana brutto przelicza netto). Oferta liczy brutto według swojej stawki VAT.' },
      { area: 'ogolne', text: 'Po każdej aktualizacji pojawia się ten panel z listą zmian; wcześniejsze zmiany są w menu użytkownika → „Co nowego”. Gdy CRM był otwarty w trakcie aktualizacji, w rogu pojawi się prośba o odświeżenie strony.' },
      { area: 'strefa', text: 'Strefa klienta odświeża się sama, a przy Wiadomościach widać liczbę nieprzeczytanych odpowiedzi biura.' },
      { area: 'planowanie', text: 'Lider wybrany przy zakładaniu ekipy od razu wchodzi do jej stałego składu.' },
      { area: 'teren', text: 'Aktywacja aplikacji terenowej na telefonie po innym pracowniku działa bez czyszczenia danych aplikacji.' },
    ],
  },
];

/** Punkty widoczne dla osoby z danymi uprawnieniami. */
export function releaseItemsFor(note: ReleaseNote, has: (permission: string) => boolean): ReleaseNote['items'] {
  return note.items.filter((i) => {
    const perms: readonly string[] | null = RELEASE_AREAS[i.area].permissions;
    return !perms || perms.some(has);
  });
}

/**
 * Wpisy, których osoba jeszcze nie widziała: nowsze niż ostatnio obejrzany. Ktoś, kto panelu jeszcze nie oglądał
 * (nowe konto, pierwsza wersja z panelem), widzi tylko najnowszy wpis.
 */
export function unseenReleaseNotes(seen: string | null, notes: ReleaseNote[] = RELEASE_NOTES): ReleaseNote[] {
  if (!seen) return notes.slice(0, 1);
  return notes.filter((n) => n.id.localeCompare(seen, 'en', { numeric: true }) > 0);
}

export interface ReleaseSeenDto {
  seen: string | null;
}
export const releaseSeenSchema = z.object({ id: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}(-\d+)?$/, 'Nieprawidłowy wpis listy zmian') });
